<template>
  <div v-if="apiConfig" class="patient-result-history case-creation__step" v-loading="loading">
    <el-row :gutter="20">
      <el-col :span="24">
        <div class="case-creation__card attachments border-bottom-none">
          <div class="case-creation__heading">
            <div class="title-with-icon">
              <span class="icon">
                <img src="@/assets/images/icons/icon-test-reports.svg" alt="icon" class="img-fluid" />
              </span>
              <h3 class="title">Patient Result History</h3>
            </div>
          </div>

          <div v-if="patientName" class="prh-patient-bar">
            <span class="prh-patient-icon">&#9679;</span>
            <span class="prh-patient-name">{{ patientName }}</span>
          </div>

          <el-alert v-if="errorMsg" :title="errorMsg" type="error" :closable="false" class="prh-alert" />

          <div v-else-if="!loading && !hasResults" class="prh-empty">
            <img src="@/assets/images/icons/icon-test-reports.svg" alt="" class="prh-empty-icon" />
            <p>No historical results found for this patient.</p>
          </div>

          <!-- API 1: Clinical Chemistry — pivot by category, all accessions as columns -->
          <div v-else-if="apiConfig.api === 1" class="prh-pivot-layout">
            <div v-for="(group, gi) in api1PivotGroups" :key="gi" class="prh-result-group">
              <div v-if="group.category" class="prh-group-header">
                <span class="prh-group-pill">{{ group.category }}</span>
              </div>
              <div class="prh-pivot-scroll">
                <table class="prh-pivot-table">
                  <thead>
                    <tr>
                      <th class="prh-pivot-th prh-pivot-th--name">Test Name</th>
                      <th
                        v-for="(item, ci) in resultItems" :key="ci"
                        class="prh-pivot-th"
                        :class="{ 'prh-pivot-th--current': item.accession_id === caseDetails.accession_id }"
                      >
                        <el-tag v-if="item.accession_id === caseDetails.accession_id" type="success" size="small" class="prh-current-badge">CURRENT</el-tag>
                        <div class="prh-pivot-acc">{{ item.accession_id }}</div>
                        <div class="prh-pivot-date">{{ formatDate(item.created_at) }}</div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(row, ri) in group.testRows" :key="ri" class="prh-pivot-tr">
                      <td class="prh-pivot-td prh-pivot-td--name">
                        <span class="prh-test-name">{{ row.test_name }}</span>
                        <span v-if="row.units" class="prh-pivot-units">{{ row.units }}</span>
                      </td>
                      <td
                        v-for="(res, ci) in row.results" :key="ci"
                        class="prh-pivot-td"
                        :class="{ 'prh-pivot-td--current': resultItems[ci].accession_id === caseDetails.accession_id }"
                      >
                        <template v-if="res">
                          <span :class="['prh-pivot-val', res.flag ? getFlagPillClass(res.flag) : '']">{{ res.result || '—' }}</span>
                          <span v-if="res.flag" :class="['flag-pill', getFlagPillClass(res.flag)]">{{ res.flag }}</span>
                        </template>
                        <span v-else class="prh-no-flag">—</span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <!-- API 2: Sendout — pivot by type, all accessions as columns -->
          <div v-else-if="apiConfig.api === 2" class="prh-pivot-layout">
            <div v-for="(group, gi) in api2PivotGroups" :key="gi" class="prh-result-group">
              <div v-if="group.type" class="prh-group-header">
                <span class="prh-group-pill">{{ group.type }}</span>
              </div>
              <div class="prh-pivot-scroll">
                <table class="prh-pivot-table">
                  <thead>
                    <tr>
                      <th class="prh-pivot-th prh-pivot-th--name">Test Name</th>
                      <th
                        v-for="(item, ci) in resultItems" :key="ci"
                        class="prh-pivot-th"
                        :class="{ 'prh-pivot-th--current': item.accession_id === caseDetails.accession_id }"
                      >
                        <el-tag v-if="item.accession_id === caseDetails.accession_id" type="success" size="small" class="prh-current-badge">CURRENT</el-tag>
                        <div class="prh-pivot-acc">{{ item.accession_id }}</div>
                        <div class="prh-pivot-date">{{ formatDate(item.created_at) }}</div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(row, ri) in group.testRows" :key="ri" class="prh-pivot-tr">
                      <td class="prh-pivot-td prh-pivot-td--name">
                        <span class="prh-test-name">{{ row.test_name }}</span>
                      </td>
                      <td
                        v-for="(res, ci) in row.results" :key="ci"
                        class="prh-pivot-td"
                        :class="{ 'prh-pivot-td--current': resultItems[ci].accession_id === caseDetails.accession_id }"
                      >
                        <template v-if="res">
                          <span v-if="res.result_nature" :class="['tox-outcome-pill', getResultNatureClass(res.result_nature)]">{{ res.result_nature }}</span>
                          <span v-if="res.flags" :class="['flag-pill', getFlagPillClass(res.flags)]">{{ res.flags }}</span>
                          <span v-if="!res.result_nature && !res.flags" class="prh-no-flag">—</span>
                        </template>
                        <span v-else class="prh-no-flag">—</span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <!-- API 3: Toxicology in-house -->
          <div v-else-if="apiConfig.api === 3" class="prh-tox-layout">

            <!-- Left sidebar -->
            <div class="prh-tox-sidebar">
              <div class="prh-tox-scroll-track">
                <div
                  v-if="currentToxResult"
                  class="prh-tox-thumb prh-tox-thumb--current"
                  :class="{ 'prh-tox-thumb--active': selectedKey === 'current' }"
                  @click="selectCase('current')"
                >
                  <el-tag type="success" size="small" class="prh-current-badge">CURRENT</el-tag>
                  <div class="prh-tox-thumb-acc">{{ currentToxResult.accession_id || currentToxResult.sample_name }}</div>
                  <div class="prh-tox-thumb-date">{{ formatDate(currentToxResult.created_at) }}</div>
                  <div class="prh-tox-thumb-badges">
                    <span v-if="getStatusValue(currentToxResult)" :class="['fr-badge', getStatusClass(currentToxResult)]">{{ getStatusValue(currentToxResult) }}</span>
                  </div>
                </div>

                <div v-if="toxHistoryItems.length" class="prh-tox-scroll-divider"><span>Previous</span></div>

                <div
                  v-for="(item, idx) in toxHistoryItems"
                  :key="idx"
                  class="prh-tox-thumb"
                  :class="{ 'prh-tox-thumb--active': selectedKey === idx }"
                  @click="selectCase(idx)"
                >
                  <div class="prh-tox-thumb-acc">{{ item.accession_id }}</div>
                  <div class="prh-tox-thumb-date">{{ formatDate(item.created_at) }}</div>
                  <div class="prh-tox-thumb-badges">
                    <span v-if="getStatusValue(item)" :class="['fr-badge', getStatusClass(item)]">{{ getStatusValue(item) }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Right detail panel -->
            <div class="prh-tox-detail">
              <div v-if="selectedItem">

                <!-- Detail header -->
                <div class="prh-tox-detail-header">
                  <div class="prh-detail-header-left">
                    <span class="prh-tox-detail-acc">{{ selectedItem.accession_id || selectedItem.sample_name }}</span>
                    <span v-if="getStatusValue(selectedItem)" :class="['fr-badge', getStatusClass(selectedItem)]">{{ getStatusValue(selectedItem) }}</span>
                  </div>
                  <span class="prh-tox-detail-date">{{ formatDate(selectedItem.created_at) }}</span>
                </div>

                <!-- Detail body -->
                <div class="prh-tox-detail-body">

                  <!-- Prescribed medications -->
                  <div v-if="toxMedications.length" class="tox-medications">
                    <span class="tox-med-label">Prescribed Medications</span>
                    <span v-for="(m, i) in toxMedications" :key="i" class="tox-med-pill">{{ m }}</span>
                  </div>

                  <!-- Summary counts (when detail rows have no report_test_name) -->
                  <div v-if="toxHasAnySummaryData && !toxHasProcessedRows" class="tox-summary-counts">
                    <div v-if="toxConsistentItems.length" class="tox-count-chip tox-count-chip--consistent">
                      <span class="tox-count-num">{{ toxConsistentItems.length }}</span>
                      <span class="tox-count-lbl">Consistent</span>
                    </div>
                    <div v-if="toxInconsistentNoMed.length" class="tox-count-chip tox-count-chip--inconsistent">
                      <span class="tox-count-num">{{ toxInconsistentNoMed.length }}</span>
                      <span class="tox-count-lbl">Inconsistent (No Medication)</span>
                    </div>
                    <div v-if="toxInconsistentMed.length" class="tox-count-chip tox-count-chip--inconsistent-med">
                      <span class="tox-count-num">{{ toxInconsistentMed.length }}</span>
                      <span class="tox-count-lbl">Inconsistent (Medication)</span>
                    </div>
                  </div>

                  <!-- Screen Validity -->
                  <div v-if="toxHasProcessedScreen" class="tox-section">
                    <h5 class="tox-section-title">Screen Validity</h5>
                    <el-table :data="toxScreenValidity" size="small" stripe class="prh-table">
                      <el-table-column label="Test" min-width="200">
                        <template #default="scope">
                          <span class="prh-test-name">{{ scope.row.report_test_name || scope.row['Full name'] || '—' }}</span>
                        </template>
                      </el-table-column>
                      <el-table-column label="Interpretation" min-width="160">
                        <template #default="scope">
                          <span :class="getToxResultClass(scope.row.result_value)">{{ scope.row.result_value || '—' }}</span>
                        </template>
                      </el-table-column>
                    </el-table>
                  </div>

                  <!-- Results grouped by category (confirmation + etg_ets + screen) -->
                  <div v-if="toxHasProcessedRows" class="tox-section">
                    <h5 class="tox-section-title">Results</h5>
                    <div v-for="(group, gi) in toxCategoryGroups" :key="gi" class="tox-cat-block">
                      <div v-if="group.name" class="tox-cat-header">
                        <span class="tox-cat-pill">{{ group.name }}</span>
                      </div>
                      <el-table :data="group.results" size="small" class="prh-table">
                        <el-table-column label="Drug / Test" min-width="190" fixed="left">
                          <template #default="scope">
                            <span class="prh-test-name">{{ scope.row.report_test_name || scope.row.test_name || '—' }}</span>
                          </template>
                        </el-table-column>
                        <el-table-column label="Outcome" min-width="130" align="center">
                          <template #default="scope">
                            <span :class="['tox-outcome-pill', getToxFlagPillClass(scope.row.flag)]">{{ scope.row.flag || '—' }}</span>
                          </template>
                        </el-table-column>
                        <el-table-column label="Interpretation" min-width="150">
                          <template #default="scope">
                            <span :class="['tox-interp', getToxInterpretationClass(scope.row.flag)]">{{ scope.row.interpretation || scope.row.result_value || '—' }}</span>
                          </template>
                        </el-table-column>
                        <el-table-column label="Previous Cases" min-width="240">
                          <template #default="scope">
                            <div class="tox-hist-chips">
                              <span
                                v-for="(h, hi) in (toxHistoryDrugMap[scope.row.report_test_name || scope.row.test_name] || [])"
                                :key="hi"
                                :class="['tox-hist-chip', getToxFlagPillClass(h.flag)]"
                                :title="formatDate(h.date)"
                              >
                                <span class="tox-hist-acc">{{ h.accession_id }}</span>
                                <span :class="['tox-hist-flag', getToxFlagPillClass(h.flag)]">{{ h.flag || '—' }}</span>
                              </span>
                              <span v-if="!(toxHistoryDrugMap[scope.row.report_test_name || scope.row.test_name] || []).length" class="tox-hist-none">No prior data</span>
                            </div>
                          </template>
                        </el-table-column>
                      </el-table>
                    </div>
                  </div>

                  <div v-if="!toxHasData" class="tox-no-data">No result details available.</div>
                </div>
              </div>
              <div v-else class="prh-tox-empty">Select a case to view details.</div>
            </div>

          </div>

          <!-- API 4: UTI / STI / WOMENS_HEALTH / RESPIRATORY — pivot across visits -->
          <div v-else-if="apiConfig.api === 4" class="prh-pivot-layout">
            <div v-if="api4PivotData.groups.length" class="prh-pivot-scroll">
              <table class="prh-pivot-table positive-negative-table">
                <thead>
                  <tr>
                    <th class="prh-pivot-th prh-pivot-th--name">Assay</th>
                    <th
                      v-for="(item, ci) in api4PivotData.items"
                      :key="ci"
                      :class="['prh-pivot-th', item.sample_name === caseDetails.accession_id ? 'prh-pivot-th--current' : '']"
                    >
                      <div class="prh-pivot-acc">{{ item.sample_name }}</div>
                      <div class="prh-pivot-date">{{ formatDate(item.created_at) }}</div>
                      <span v-if="item.sample_name === caseDetails.accession_id" class="prh-current-badge">CURRENT</span>
                      <span
                        v-if="item.result_summary && item.result_summary.final_result"
                        :class="['fr-badge', item.result_summary.final_result.result_status === 'POSITIVE' ? 'fr-badge--positive' : 'fr-badge--negative']"
                        style="margin-top:4px"
                      >{{ item.result_summary.final_result.result_status }}</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <template v-for="(group, gi) in api4PivotData.groups" :key="gi">
                    <tr class="prh-pivot-group-row">
                      <td :colspan="api4PivotData.items.length + 1" class="prh-pivot-group-td">
                        <span class="prh-group-pill">{{ group.label }}</span>
                      </td>
                    </tr>
                    <tr v-for="(row, ri) in group.rows" :key="ri" class="prh-pivot-tr">
                      <td class="prh-pivot-td prh-pivot-td--name">
                        <span class="prh-test-name">{{ row.display_name }}</span>
                      </td>
                      <td
                        v-for="(res, ci) in row.results"
                        :key="ci"
                        :class="['prh-pivot-td', api4PivotData.items[ci] && api4PivotData.items[ci].sample_name === caseDetails.accession_id ? 'prh-pivot-td--current' : '']"
                      >
                        <span v-if="res && res.level && res.level !== '-'" class="assay-vlaues-level">
                          <span :class="getLevelClass(res.level)">{{ res.level }}</span>
                        </span>
                        <span v-else class="prh-no-flag">—</span>
                      </td>
                    </tr>
                  </template>
                </tbody>
              </table>
            </div>
            <div v-else class="tox-no-data">No assay results available.</div>
          </div>

        </div>
      </el-col>
    </el-row>
  </div>
</template>

<script>
import $axios from "@/config/axios";

function normalizeItem(raw) {
  if (!raw) return null;
  const rs = raw.result_summary || {};
  const pickArr = (rootVal, rsVal) => {
    const rArr  = Array.isArray(rsVal)   ? rsVal   : [];
    const rRoot = Array.isArray(rootVal) ? rootVal : [];
    return rArr.length ? rArr : rRoot;
  };

  return {
    _id:           raw._id,
    accession_id:  raw.accession_id,
    sample_name:   raw.sample_name,
    created_at:    raw.created_at,
    result_type:   raw.result_type  || rs.result_type  || null,
    final_result:  raw.final_result || rs.final_result || null,
    prescribed_medications: pickArr(raw.prescribed_medications, rs.prescribed_medications),
    screen_validity:        pickArr(raw.screen_validity,        rs.screen_validity),
    screen_results:         pickArr(raw.screen_results,         rs.screen_results),
    confirmation_results:   pickArr(raw.confirmation_results,   rs.confirmation_results),
    etg_ets_results:        pickArr(raw.etg_ets_results,        rs.etg_ets_results),
    confirmation_summary_consistent:
      pickArr(raw.confirmation_summary_consistent,               rs.confirmation_summary_consistent),
    confirmation_summary_inconsistent_no_medication:
      pickArr(raw.confirmation_summary_inconsistent_no_medication, rs.confirmation_summary_inconsistent_no_medication),
    confirmation_summary_inconsistent_medication:
      pickArr(raw.confirmation_summary_inconsistent_medication,  rs.confirmation_summary_inconsistent_medication),
  };
}

export default {
  name: "PatientResultHistory",
  props: {
    caseDetails: { type: Object, default: null },
    // api4 (/patientResults) data is fetched once by the parent (ViewCase) and
    // passed down, so this component does not re-fetch that endpoint.
    patientResults: { type: Array, default: () => [] },
  },
  data() {
    return {
      loading: false,
      errorMsg: null,
      resultItems: [],
      currentToxResult: null,
      toxHistoryItems: [],
      selectedKey: "current",
      selectedApi1Key: 0,
      selectedApi2Key: 0,
      api4Items: [],
      fetchSeq: 0,
    };
  },
  computed: {
    apiConfig() {
      const c = this.caseDetails;
      if (!c) return null;
      const types = c.case_types || [];

      if (c.is_labdaq_order || (types.includes("CLINICAL_CHEMISTRY") && c.is_sendout)) {
        const pid = c.patient_info?.internal_patient_id || c.internal_patient_id || c.patient_info?._id || null;
        if (!pid) return null;
        return { api: 1, endpoint: "/patient-case-results/clinical-chemistry-sendout", payload: { internal_patient_id: pid, case_types: "CLINICAL_CHEMISTRY" } };
      }
      if (c.is_sendout) {
        const pid = c.patient_info?.internal_patient_id || c.internal_patient_id || c.patient_info?._id || null;
        if (!pid) return null;
        return { api: 2, endpoint: "/patient-case-results/allcasetypessendouts", payload: { internal_patient_id: pid, case_types: types[0] || "" } };
      }
      if (types.includes("TOXICOLOGY")) {
        return { api: 3, endpoint: "/patient-case-results/toxicology-inhouse", payload: { accession_id: c.accession_id, case_types: "TOXICOLOGY" } };
      }
      const api4Types = ["UTI", "STI", "WOMENS_HEALTH", "RESPIRATORY_PATHOGEN_PANEL"];
      const matchedApi4Type = types.find(t => api4Types.includes(t));
      if (matchedApi4Type && !c.is_sendout) {
        const pi = c.patient_info || {};
        const dob = (pi.date_of_birth || "").split("T")[0] || null;
        if (!pi.first_name || !pi.last_name || !dob) return null;
        return {
          api: 4,
          endpoint: `${process.env.VUE_APP_SERVICES_URL}/patientResults`,
          payload: {
            case_type: matchedApi4Type,
            firstName: pi.first_name,
            lastName: pi.last_name,
            dob,
            gender: pi.gender || "",
            lab_code: c.lab?.lab_code || "",
          },
        };
      }
      return null;
    },
    hasResults() {
      if (!this.apiConfig) return false;
      if (this.apiConfig.api === 3) return !!(this.currentToxResult || this.toxHistoryItems.length);
      if (this.apiConfig.api === 4) return this.api4Items.length > 0;
      return this.resultItems.length > 0;
    },
    selectedItem() {
      if (this.selectedKey === "current") return this.currentToxResult;
      return this.toxHistoryItems[this.selectedKey] || null;
    },

    /* ── Selected item computed helpers ── */
    toxFinalResult() { return this.selectedItem?.final_result || null; },
    toxFinalResultValue() {
      const fr = this.toxFinalResult;
      return fr?.confirmation_final_result || fr?.screening_final_result || null;
    },
    toxFinalResultClass() {
      const v = (this.toxFinalResultValue || "").toUpperCase();
      if (v === "POSITIVE") return "tox-final--positive";
      if (v === "NEGATIVE") return "tox-final--negative";
      return "";
    },
    toxStatusClass() {
      const v = (this.toxFinalResult?.result_status || "").toUpperCase();
      if (v === "INCONSISTENT") return "tox-status--inconsistent";
      if (v === "CONSISTENT")   return "tox-status--consistent";
      return "";
    },
    toxMedications()      { return this.selectedItem?.prescribed_medications || []; },
    toxScreenValidity()   { return this.selectedItem?.screen_validity || []; },
    toxScreenResults()    { return this.selectedItem?.screen_results  || []; },
    toxConfirmation()     { return this.selectedItem?.confirmation_results || []; },
    toxEtgEts()           { return this.selectedItem?.etg_ets_results || []; },
    toxConsistentItems()  { return this.selectedItem?.confirmation_summary_consistent || []; },
    toxInconsistentNoMed(){ return this.selectedItem?.confirmation_summary_inconsistent_no_medication || []; },
    toxInconsistentMed()  { return this.selectedItem?.confirmation_summary_inconsistent_medication || []; },

    toxAllRows() {
      const rows = [...this.toxConfirmation, ...this.toxEtgEts, ...this.toxScreenResults];
      return rows.sort((a, b) =>
        ("" + (a.category_code || "")).localeCompare("" + (b.category_code || ""))
      );
    },
    toxHasProcessedRows() {
      return this.toxAllRows.length > 0 && !!this.toxAllRows[0]?.report_test_name;
    },
    toxCategoryGroups() {
      const cats = [...new Set(this.toxAllRows.map(r => r.category || ""))];
      return cats.map(cat => ({
        name: cat,
        results: this.toxAllRows.filter(r => (r.category || "") === cat),
      }));
    },
    toxHasProcessedScreen() {
      return this.toxScreenValidity.length > 0 && !!this.toxScreenValidity[0]?.report_test_name;
    },
    toxHasAnySummaryData() {
      return !!(this.toxConsistentItems.length || this.toxInconsistentNoMed.length || this.toxInconsistentMed.length);
    },
    toxHasData() {
      const result = !!(this.toxFinalResult || this.toxMedications.length || this.toxHasProcessedRows ||
                this.toxHasProcessedScreen || this.toxHasAnySummaryData);
      console.log("[PRH] toxHasData:", result, {
        toxFinalResult: this.toxFinalResult,
        toxMedications: this.toxMedications,
        toxHasProcessedRows: this.toxHasProcessedRows,
        toxAllRowsCount: this.toxAllRows.length,
        toxHasAnySummaryData: this.toxHasAnySummaryData,
        selectedItem: this.selectedItem,
      });
      return result;
    },

    selectedApi1Item() {
      return this.resultItems[this.selectedApi1Key] || null;
    },
    selectedApi2Item() {
      return this.resultItems[this.selectedApi2Key] || null;
    },
    api1PivotGroups() {
      if (!this.resultItems.length) return [];
      const allRows = this.resultItems.flatMap(item => item.results || []);
      const cats = [...new Set(allRows.map(r => r.category || ""))].sort();
      return cats.map(cat => {
        const testNames = [...new Set(
          this.resultItems.flatMap(item =>
            (item.results || []).filter(r => (r.category || "") === cat).map(r => r.test_name)
          )
        )];
        return {
          category: cat || null,
          testRows: testNames.map(tn => ({
            test_name: tn,
            units: this.resultItems.flatMap(i => i.results || []).find(r => r.test_name === tn)?.units || "",
            results: this.resultItems.map(item =>
              (item.results || []).find(r => r.test_name === tn && (r.category || "") === cat) || null
            ),
          })),
        };
      });
    },
    api2PivotGroups() {
      if (!this.resultItems.length) return [];
      const allRows = this.resultItems.flatMap(item => item.discrete_results || []);
      const types = [...new Set(allRows.map(r => r.type || ""))].sort((a, b) => b.localeCompare(a));
      return types.map(type => {
        const testNames = [...new Set(
          this.resultItems.flatMap(item =>
            (item.discrete_results || []).filter(r => (r.type || "") === type).map(r => r.display_name || r.assay_id)
          )
        )];
        return {
          type: type || null,
          testRows: testNames.map(tn => ({
            test_name: tn,
            results: this.resultItems.map(item =>
              (item.discrete_results || []).find(r => (r.display_name || r.assay_id) === tn && (r.type || "") === type) || null
            ),
          })),
        };
      });
    },
    api4PivotData() {
      if (!this.api4Items.length) return { items: [], groups: [] };
      const items = [...this.api4Items].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

      // Collect unique assays preserving first-seen order, keyed by display_name
      const assayMeta = {};
      for (const item of items) {
        for (const a of (item.result_summary?.result_assays || [])) {
          const key = a.display_name || a.assay_name;
          if (!assayMeta[key]) assayMeta[key] = { isAbr: !!a.antibiotic_resistance };
        }
      }

      const makeRows = (keys) => keys.map(key => ({
        display_name: key,
        results: items.map(item =>
          (item.result_summary?.result_assays || []).find(a => (a.display_name || a.assay_name) === key) || null
        ),
      }));

      const organisms = Object.keys(assayMeta).filter(k => !assayMeta[k].isAbr);
      const abrGenes  = Object.keys(assayMeta).filter(k =>  assayMeta[k].isAbr);

      const groups = [];
      if (organisms.length) groups.push({ label: "UTI PATHOGENS", rows: makeRows(organisms) });
      if (abrGenes.length)  groups.push({ label: "ANTIBIOTIC RESISTANCE GENES", rows: makeRows(abrGenes) });

      return { items, groups };
    },
    patientName() {
      const pi = this.caseDetails?.patient_info;
      if (!pi) return null;
      if (pi.full_name) return pi.full_name;
      if (pi.patient_name) return pi.patient_name;
      const parts = [pi.first_name, pi.last_name].filter(Boolean);
      return parts.length ? parts.join(" ") : (pi.name || null);
    },

    /* ── History drug comparison map ── */
    toxHistoryDrugMap() {
      const otherItems = this.selectedKey === "current"
        ? this.toxHistoryItems
        : [
            ...(this.currentToxResult ? [this.currentToxResult] : []),
            ...this.toxHistoryItems.filter((_, i) => i !== this.selectedKey),
          ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

      const map = {};
      for (const item of otherItems) {
        const allRows = [
          ...(item.confirmation_results || []),
          ...(item.etg_ets_results || []),
          ...(item.screen_results || []),
        ];
        for (const row of allRows) {
          const key = row.report_test_name || row.test_name;
          if (!key) continue;
          if (!map[key]) map[key] = [];
          map[key].push({
            accession_id: item.accession_id,
            date: item.created_at,
            flag: row.flag,
          });
        }
      }
      return map;
    },
  },
  watch: {
    'caseDetails._id': { immediate: true, handler(val) { if (val) this.fetchResults(); } },
    patientResults: {
      immediate: true,
      handler(val) {
        if (this.apiConfig && this.apiConfig.api === 4) {
          this.api4Items = Array.isArray(val) ? val : [];
        }
      },
    },
  },
  methods: {
    selectCase(key) { this.selectedKey = key; },

    getStatusValue(item) { return item?.final_result?.result_status || null; },
    getStatusClass(item) {
      const v = (item?.final_result?.result_status || "").toUpperCase();
      if (v === "INCONSISTENT") return "fr-badge--inconsistent";
      if (v === "CONSISTENT")   return "fr-badge--consistent";
      return "fr-badge--neutral";
    },
    getToxResultClass(val) {
      const v = (val || "").toString().toUpperCase();
      if (v === "POSITIVE" || v === "ABNORMAL") return "tox-positive";
      if (v === "NEGATIVE" || v === "NORMAL")   return "tox-negative";
      return "";
    },
    getToxFlagPillClass(flag) {
      const f = (flag || "").toUpperCase();
      if (f === "POSITIVE")     return "pill--positive";
      if (f === "NOT DETECTED") return "pill--not-detected";
      if (f === "ABNORMAL")     return "pill--abnormal";
      return "pill--neutral";
    },
    getToxInterpretationClass(flag) {
      const f = (flag || "").toUpperCase();
      if (f === "POSITIVE") return "interp--positive";
      if (f === "ABNORMAL") return "interp--abnormal";
      return "";
    },

    async fetchResults() {
      if (!this.apiConfig) return;
      // api4 (/patientResults) is provided by the parent via the patientResults
      // prop — synced through the watcher — so avoid a duplicate request here.
      if (this.apiConfig.api === 4) {
        this.api4Items = Array.isArray(this.patientResults) ? this.patientResults : [];
        return;
      }
      const seq = ++this.fetchSeq;
      this.loading = true;
      this.errorMsg = null;
      this.resultItems = [];
      this.currentToxResult = null;
      this.toxHistoryItems = [];
      this.selectedKey = "current";
      this.selectedApi1Key = 0;
      this.selectedApi2Key = 0;
      this.api4Items = [];
      try {
        const res = await $axios.post(this.apiConfig.endpoint, this.apiConfig.payload);
        if (seq !== this.fetchSeq) return;
        const data = res.data;
        if (this.apiConfig.api === 3) {
          console.log("[PRH] raw API response:", JSON.parse(JSON.stringify(data)));
          this.currentToxResult = normalizeItem(data.current_result) || null;
          console.log("[PRH] currentToxResult after normalize:", JSON.parse(JSON.stringify(this.currentToxResult || {})));
          const history = Array.isArray(data.data) ? data.data.map(normalizeItem) : [];
          this.toxHistoryItems = history.sort(
            (a, b) => new Date(b.created_at) - new Date(a.created_at)
          );
          console.log("[PRH] toxHistoryItems[0] after normalize:", this.toxHistoryItems[0] ? JSON.parse(JSON.stringify(this.toxHistoryItems[0])) : null);
        } else {
          this.resultItems = Array.isArray(data.data) ? data.data : [];
        }
      } catch (err) {
        if (seq === this.fetchSeq) {
          this.errorMsg = err?.response?.data?.message || "Failed to load patient history.";
        }
      } finally {
        if (seq === this.fetchSeq) this.loading = false;
      }
    },
    getResultsForItem(item) { return item.discrete_results || item.results || []; },
    getGroupedByCategory(item) {
      const rows = item.results || [];
      const cats = [...new Set(rows.map(r => r.category || ""))].sort();
      if (cats.length === 1 && cats[0] === "") return [{ category: null, results: rows }];
      return cats.map(cat => ({
        category: cat || null,
        results: rows.filter(r => (r.category || "") === cat),
      }));
    },
    getGroupedResultsForItem(item) {
      const rows = this.getResultsForItem(item);
      const hasType = rows.some(r => r.type);
      if (!hasType) return [{ type: null, results: rows }];
      const typeOrder = [...new Set(rows.map(r => r.type || ""))].sort((a, b) => b.localeCompare(a));
      return typeOrder.map(t => ({
        type: t || null,
        results: rows.filter(r => (r.type || "") === t),
      }));
    },
    getFlagPillClass(flag) {
      const f = (flag || "").toUpperCase().trim();
      if (!f) return "";
      if (f === "HH" || f === "H*" || f === "LL" || f === "L*" || f === "AA" || f === "A*") return "flag-pill--critical";
      if (f === "H" || f === "A") return "flag-pill--high";
      if (f === "L") return "flag-pill--low";
      return "flag-pill--neutral";
    },
    getLevelClass(level) {
      const l = (level || "").toUpperCase();
      if (l === "L") return "Low";
      if (l === "M") return "Med";
      if (l === "H") return "High";
      return "";
    },
    getResultNatureClass(nature) {
      const v = (nature || "").toUpperCase();
      if (v === "POSITIVE") return "pill--positive";
      if (v === "NEGATIVE") return "pill--not-detected";
      return "pill--neutral";
    },
    getFlagClass(flag, result) {
      const text = (flag || "").toString().toUpperCase();
      if (result === null || result === undefined || result === "") return "flag-empty";
      if (text.includes("CRITICAL")) return "flag-critical";
      if (text.includes("HIGH") || text.includes("LOW")) return "flag-highlow";
      return "flag-normal";
    },
    formatDate(iso) {
      if (!iso) return "—";
      const d = new Date(iso);
      if (isNaN(d)) return iso;
      return d.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" });
    },
  },
};
</script>

<style lang="scss" scoped>
.patient-result-history {
  .prh-alert { margin: 12px 20px; }

  .prh-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 40px 20px;
    color: #828282;
    font-size: 14px;
    .prh-empty-icon { width: 40px; opacity: 0.3; margin-bottom: 10px; }
  }

  /* ── API 1 & 2 ── */
  .prh-cases { padding: 16px 20px; }
  .prh-case-card { border: 1px solid #e7e7f7; border-radius: 8px; margin-bottom: 12px; overflow: hidden; }
  .prh-case-header { display: flex; align-items: center; justify-content: space-between; padding: 10px 16px; background-color: #f5f6fb; border-bottom: 1px solid #e7e7f7; }
  .prh-case-header-left { display: flex; align-items: center; gap: 8px; }
  .prh-accession { font-weight: 600; font-size: 14px; color: #273480; font-family: monospace; letter-spacing: 0.5px; }
  .prh-date { font-size: 12px; color: #828282; font-weight: 500; }
  .prh-table-wrap { padding: 12px 16px; }
  .prh-result-group { margin-bottom: 14px; }
  .prh-group-header { margin-bottom: 6px; position: sticky; left: 0; }
  .prh-group-pill {
    display: inline-block;
    background: #273480;
    color: #fff;
    border-radius: 5px;
    padding: 3px 12px;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.6px;
  }
  .prh-test-name { font-weight: 600; color: #273480; font-size: 12px; }
  .prh-no-flag { color: #c0c4cc; }

  .flag-pill {
    display: inline-block;
    padding: 2px 9px;
    border-radius: 10px;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.3px;

    &--critical { background: #ffe0e0; color: #c00;    border: 1px solid #ffb0b0; }
    &--high     { background: #fff3e0; color: #b85c00; border: 1px solid #f0c080; }
    &--low      { background: #e8f0ff; color: #1a3acc; border: 1px solid #b0c4f8; }
    &--neutral  { background: #f0f0f4; color: #555;    border: 1px solid #ddd; }
  }
  .flag-critical { background: #ec212526 !important; color: #fc0d0d !important; border-color: transparent !important; }
  .flag-highlow  { background: #f7983933 !important; color: #bf7c01 !important; border-color: transparent !important; }
  .flag-normal   { background: transparent !important; color: #222 !important; }
  .flag-empty    { color: #999 !important; }

  /* ── Pivot layout (API 1 & 2) ── */
  .prh-pivot-layout {
    padding: 16px 20px;
    border-top: 1px solid #e7e7f7;
    max-height: 560px;
    overflow: auto;
  }
  .prh-pivot-table {
    width: max-content;
    min-width: 100%;
    border-collapse: collapse;
    font-size: 12px;

    .prh-pivot-th {
      position: sticky;
      top: 0;
      z-index: 2;
      background: #273480;
      color: #fff;
      padding: 8px 14px;
      text-align: center;
      font-size: 11px;
      font-weight: 700;
      white-space: nowrap;
      min-width: 150px;
      border-left: 1px solid rgba(255,255,255,0.12);

      &--name {
        text-align: left;
        min-width: 200px;
        border-left: none;
      }
      &--current {
        background: #1a6b2a;
      }
      .prh-current-badge { margin-bottom: 4px; }
      .prh-pivot-acc { font-family: monospace; font-size: 11px; font-weight: 700; }
      .prh-pivot-date { font-size: 10px; font-weight: 400; opacity: 0.8; margin-top: 2px; }
    }

    .prh-pivot-tr {
      &:nth-child(even) td { background: #f8f9fc; }
      &:hover td { background: #eef0fb; }
    }

    .prh-pivot-td {
      padding: 7px 14px;
      border-bottom: 1px solid #eee;
      border-left: 1px solid #eee;
      text-align: center;
      vertical-align: middle;

      &--name {
        text-align: left;
        border-left: none;
        background: #fff !important;
        position: sticky;
        left: 0;
        z-index: 1;
        box-shadow: 2px 0 6px rgba(0,0,0,0.06);
      }
      &--current { background: #f0fbf0 !important; border-left: 2px solid #4bb543; }
    }

    .prh-pivot-val {
      font-weight: 600;
      font-size: 12px;
      color: #222;
      margin-right: 4px;
      &.flag-pill--critical { color: #c00; }
      &.flag-pill--high     { color: #b85c00; }
      &.flag-pill--low      { color: #1a3acc; }
    }
    .prh-pivot-units { font-size: 10px; color: #9499b8; margin-left: 4px; }
  }

  /* ── API 3 layout ── */
  .prh-patient-bar {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 20px;
    background: #f0f3fb;
    border-top: 1px solid #e7e7f7;
    border-bottom: 1px solid #dde0f0;

    .prh-patient-icon {
      font-size: 8px;
      color: #273480;
    }
    .prh-patient-name {
      font-size: 14px;
      font-weight: 700;
      color: #273480;
      letter-spacing: 0.2px;
    }
  }

  .prh-tox-layout { display: flex; min-height: 300px; border-top: 1px solid #e7e7f7; }

  /* ── Sidebar ── */
  .prh-tox-sidebar {
    flex-shrink: 0;
    width: 230px;
    border-right: 1px solid #e7e7f7;
    overflow-y: auto;
    background: #f4f5fb;
    padding: 12px 0;
    max-height: 600px;
  }
  .prh-tox-scroll-track { display: flex; flex-direction: column; }

  .prh-tox-scroll-divider {
    display: flex;
    align-items: center;
    padding: 10px 14px 6px;
    font-size: 10px;
    font-weight: 700;
    color: #9499b8;
    text-transform: uppercase;
    letter-spacing: 0.8px;
    &::after { content: ""; flex: 1; height: 1px; background: #dde0f0; margin-left: 6px; }
  }

  .prh-tox-thumb {
    margin: 3px 8px;
    padding: 10px 12px;
    cursor: pointer;
    border-radius: 8px;
    border: 1.5px solid transparent;
    background: #fff;
    box-shadow: 0 1px 3px rgba(39, 52, 128, 0.06);
    transition: all 0.15s ease;

    &:hover {
      border-color: #c7cde8;
      background: #eef0fb;
      box-shadow: 0 2px 8px rgba(39, 52, 128, 0.1);
    }
    &--active {
      border-color: #273480;
      background: #eef0fb;
      box-shadow: 0 2px 8px rgba(39, 52, 128, 0.15);
    }
    &--current.prh-tox-thumb--active {
      border-color: #4bb543;
      background: #f0fbf0;
    }
  }
  .prh-current-badge { font-weight: 700; letter-spacing: 0.5px; margin-bottom: 5px; }
  .prh-tox-thumb-acc {
    font-weight: 700;
    font-size: 12px;
    color: #273480;
    font-family: monospace;
    margin-top: 3px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    letter-spacing: 0.3px;
  }
  .prh-tox-thumb-date { font-size: 11px; color: #9499b8; margin-top: 3px; }
  .prh-tox-thumb-badges { display: flex; flex-wrap: wrap; gap: 4px; margin-top: 6px; }

  /* ── Detail panel ── */
  .prh-tox-detail { flex: 1; overflow-y: auto; max-height: 600px; overflow-x: hidden; }

  .prh-tox-detail-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 8px;
    padding: 14px 20px;
    background: linear-gradient(135deg, #273480 0%, #3b4cb5 100%);
    border-bottom: none;
  }
  .prh-detail-header-left {
    display: flex;
    align-items: center;
    gap: 10px;
  }
  .prh-tox-detail-acc {
    font-weight: 700;
    font-size: 15px;
    color: #fff;
    font-family: monospace;
    letter-spacing: 0.4px;
  }
  .prh-tox-detail-date {
    font-size: 12px;
    color: rgba(255, 255, 255, 0.75);
    font-weight: 500;
  }

  .prh-tox-detail-body { padding: 16px 20px; }

  .prh-tox-empty {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    color: #aaa;
    font-size: 13px;
    font-style: italic;
    padding: 60px 40px;
  }

  /* ── Medications ── */
  .tox-medications {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    margin-bottom: 16px;
    padding: 10px 14px;
    background: #f0f3fb;
    border-radius: 8px;
    border: 1px solid #dde0f0;
    font-size: 12px;

    .tox-med-label {
      font-weight: 700;
      color: #273480;
      font-size: 11px;
      text-transform: uppercase;
      letter-spacing: 0.4px;
      margin-right: 2px;
    }
    .tox-med-pill {
      background: #273480;
      color: #fff;
      border-radius: 12px;
      padding: 2px 10px;
      font-size: 11px;
      font-weight: 600;
    }
  }

  /* ── Summary counts ── */
  .tox-summary-counts { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 16px; }
  .tox-count-chip {
    display: flex; align-items: center; gap: 6px; padding: 6px 14px; border-radius: 20px; font-size: 12px;
    &--consistent       { background: #e6f9e6; border: 1px solid #b0e8b0; .tox-count-num { color: #2a7a2a; font-weight: 700; font-size: 15px; } .tox-count-lbl { color: #2a7a2a; font-weight: 600; } }
    &--inconsistent     { background: #fff0e0; border: 1px solid #f0c080; .tox-count-num { color: #b85c00; font-weight: 700; font-size: 15px; } .tox-count-lbl { color: #b85c00; font-weight: 600; } }
    &--inconsistent-med { background: #ffe8e8; border: 1px solid #ffb8b8; .tox-count-num { color: #c00;    font-weight: 700; font-size: 15px; } .tox-count-lbl { color: #c00;    font-weight: 600; } }
  }

  /* ── Sections & category groups ── */
  .tox-section { margin-bottom: 20px; }
  .tox-section-title {
    font-size: 13px;
    font-weight: 700;
    color: #273480;
    margin: 0 0 10px;
    padding-left: 8px;
    border-left: 3px solid #273480;
    text-transform: uppercase;
    letter-spacing: 0.4px;
  }
  .tox-cat-block { margin-bottom: 16px; }
  .tox-cat-header { margin-bottom: 6px; }
  .tox-cat-pill {
    display: inline-block;
    background: #273480;
    color: #fff;
    border-radius: 5px;
    padding: 3px 12px;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.6px;
  }
  .tox-no-data { color: #9499b8; font-size: 13px; padding: 16px 4px; font-style: italic; }

  .prh-pivot-group-row {
    background: transparent !important;
  }
  .prh-pivot-group-td {
    padding: 10px 12px 6px !important;
    border: none !important;
    background: #fff;
    position: sticky;
    left: 0;
  }

  .assay-vlaues-level {
    display: block;
    .Low {
      background: linear-gradient(270deg, rgba(54, 104, 180, 0) 0%, rgba(54, 104, 180, 0.2) 100%);
      display: block;
      padding: 2px 0 2px 10px;
      position: relative;
      font-weight: 700;
      font-size: 11px;
      color: #3668B4;
      &::after {
        content: "";
        position: absolute;
        background-color: #3668B4;
        width: 1px;
        height: 21px;
        left: 0;
        bottom: 0;
      }
    }
    .Med {
      background: linear-gradient(270deg, rgba(236, 114, 0, 0) 0%, rgba(236, 114, 0, 0.2) 100%);
      display: block;
      padding: 2px 0 2px 10px;
      position: relative;
      font-weight: 700;
      font-size: 11px;
      color: #EC7200;
      &::after {
        content: "";
        position: absolute;
        background-color: #EC7200;
        width: 1px;
        height: 21.5px;
        left: 0;
        bottom: 0;
      }
    }
    .High {
      background: linear-gradient(270deg, rgba(253, 0, 58, 0) 0%, rgba(253, 0, 58, 0.2) 100%);
      display: block;
      padding: 2px 0 2px 10px;
      position: relative;
      font-weight: 700;
      font-size: 11px;
      color: #FD003A;
      &::after {
        content: "";
        position: absolute;
        background-color: #FD003A;
        width: 1px;
        height: 22px;
        left: 0;
        bottom: 0;
      }
    }
  }

  /* ── Outcome pills ── */
  .tox-outcome-pill {
    display: inline-block;
    padding: 3px 10px;
    border-radius: 12px;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    white-space: nowrap;

    &.pill--positive     { background: #ffe8e8; color: #c00;    border: 1px solid #ffcccc; }
    &.pill--not-detected { background: #e8f9e8; color: #2a7a2a; border: 1px solid #b0e8b0; }
    &.pill--abnormal     { background: #fff3e0; color: #b85c00; border: 1px solid #f0c080; }
    &.pill--neutral      { background: #f0f0f4; color: #666;    border: 1px solid #ddd; }
  }

  /* ── Interpretation ── */
  .tox-interp {
    font-size: 12px;
    color: #444;
    &.interp--positive { color: #c00;    font-weight: 700; }
    &.interp--abnormal { color: #b85c00; font-weight: 600; }
  }

  /* ── Previous cases inline history chips ── */
  .tox-hist-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    align-items: flex-start;
  }
  .tox-hist-chip {
    display: inline-flex;
    flex-direction: column;
    align-items: flex-start;
    border-radius: 6px;
    padding: 3px 7px;
    background: #f0f0f4;
    border: 1px solid #e0e0e8;
    gap: 1px;
    cursor: default;

    .tox-hist-acc {
      font-weight: 700;
      color: #273480;
      font-family: monospace;
      font-size: 10px;
      line-height: 1.2;
    }
    .tox-hist-flag {
      font-size: 10px;
      font-weight: 700;
      line-height: 1.2;
      text-transform: uppercase;
      letter-spacing: 0.2px;
    }

    &.pill--positive {
      background: #fff0f0;
      border-color: #ffd0d0;
      .tox-hist-flag { color: #c00; }
    }
    &.pill--not-detected {
      background: #f0fbf0;
      border-color: #c0e8c0;
      .tox-hist-flag { color: #2a7a2a; }
    }
    &.pill--neutral .tox-hist-flag { color: #666; }
  }
  .tox-hist-none { color: #c0c4cc; font-size: 11px; font-style: italic; }

  /* ── Shared result helpers ── */
  .tox-positive     { color: #fc0d0d; font-weight: 600; }
  .tox-not-detected { color: #4bb543; font-weight: 600; }
  .tox-negative     { color: #4bb543; font-weight: 500; }

  /* ── Table ── */
  .prh-table {
    :deep(.el-table__header-wrapper th.el-table__cell) {
      background-color: #273480;
      color: #fff;
      font-size: 11px;
      font-weight: 700;
      padding: 7px 0;
      letter-spacing: 0.3px;
    }
    :deep(.el-table__row) {
      .el-table__cell { padding: 8px 0; }
    }
    .prh-test-name { font-weight: 600; color: #273480; font-size: 12px; }
  }
}
</style>

<style lang="scss">
.fr-badge {
  display: inline-flex;
  align-items: center;
  font-size: 10px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 10px;
  letter-spacing: 0.3px;
  text-transform: uppercase;

  &--positive     { background: #ffe8e8; color: #c00; }
  &--negative     { background: #e6f9e6; color: #2a7a2a; }
  &--inconsistent { background: #fff0e0; color: #b85c00; border: 1px solid #f0a040; }
  &--consistent   { background: #e6f9e6; color: #2a7a2a; border: 1px solid #7bcc7b; }
  &--neutral      { background: #f0f0f0; color: #555; }
}

/* fr-badge on dark header background */
.prh-tox-detail-header {
  .fr-badge {
    &--inconsistent { background: rgba(255, 160, 64, 0.25); color: #ffd080; border-color: rgba(255, 160, 64, 0.5); }
    &--consistent   { background: rgba(75, 181, 67, 0.25);  color: #a0f0a0; border-color: rgba(75, 181, 67, 0.5); }
    &--neutral      { background: rgba(255,255,255,0.15);   color: #ddd; }
  }
}
</style>
